import { lodash } from './lodash';

const getOneWithZeros = (count: number) => {
  if (count < 1) return 1;
  return Number(`1${'0'.repeat(count)}`);
};

/**
 * Adds spaces to a string every three characters for better readability.
 */
export function addSpacesEveryThree(str: string) {
  return lodash
    .chunk(str.split('').reverse(), 3)
    .map((part) => part.join(''))
    .join(' ')
    .split('')
    .reverse()
    .join('');
}

/**
 * Formats a floating-point number into a string representation.
 * @param value - The number to format.
 * @param formatInt - Function to format the integer part of the number.
 */
export const formatFloat = (
  value?: number,
  formatInt?: (value: string) => string,
  maxFloatingPoints = 2
): string => {
  if (value === undefined) return '';
  const price = `${value}`.substring(0, 13);
  const [integer, floating] = price.split('.');
  const integerFmt = formatInt?.(integer) || integer;
  const floatFmt = floating?.slice(0, maxFloatingPoints);
  return [integerFmt, floatFmt].filter(Boolean).join(',');
};

/**
 * Parses a string representation of a floating-point number into a JavaScript number.
 */
export const parseFloat = (value?: string): number => {
  if (value === undefined || value === '') return 0;
  return Number(value.replace(/\s+/g, '').replace(',', '.'));
};

/**
 * Parses a string representation of a price into a number (multiplied by 100).
 */
export const parsePrice = (
  value?: string,
  maxFloatingPoints = 2
): number | undefined => {
  if (value === '') return undefined;
  return Math.round(parseFloat(value) * getOneWithZeros(maxFloatingPoints));
};

/**
 * Formats a number into a string representation of a price.
 * @param value - The number to format.
 */
export const formatPrice = (
  value?: number,
  round = false,
  maxFloatingPoints = 2
): string => {
  if (value === undefined || Number.isNaN(value)) return '';
  const priceMultiplier = getOneWithZeros(maxFloatingPoints);
  const priveValue = value / priceMultiplier;
  const price = round ? Math.round(priveValue) : priveValue;
  return formatFloat(price, addSpacesEveryThree, maxFloatingPoints);
};

export const formatAmount = (value?: number): string => {
  if (value === undefined || Number.isNaN(value)) return '';
  return formatFloat(value, addSpacesEveryThree);
};

export const parseAmount = (value?: string): number | undefined => {
  if (value === '') return undefined;
  return parseFloat(value);
};
